import { getSessionToken } from "../context/SessionContext";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

export interface DraftPayload {
  formData: Record<string, unknown>;
  currentStep: number;
  email?: string;
  referralCode?: string;
}

export interface PortalDraft {
  token: string;
  formData: Record<string, unknown>;
  currentStep: number;
  email: string | null;
  referralCode: string | null;
  firstName: string | null;
  lastName: string | null;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
}

function authHeaders(includeJson = false, requireSession = false): HeadersInit {
  const token = getSessionToken();
  if (requireSession && !token) {
    throw new Error("Authentication required");
  }

  return {
    ...(includeJson && { "Content-Type": "application/json" }),
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

async function postDraft(payload: DraftPayload, requireSession: boolean): Promise<string> {
  const res = await fetch(`${API_BASE}/api/drafts`, {
    method: "POST",
    headers: authHeaders(true, requireSession),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Failed to create draft");
  const json = await res.json();
  return json.data.token as string;
}

/** POST /api/drafts — creates a new draft, returns the token */
export async function createDraft(payload: DraftPayload): Promise<string> {
  return postDraft(payload, false);
}

/** POST /api/drafts — creates a draft owned by the current PortalUser */
export async function createPortalDraft(payload: DraftPayload): Promise<string> {
  return postDraft(payload, true);
}

/** PUT /api/drafts/:token — updates an existing draft */
export async function updateDraft(token: string, payload: Partial<DraftPayload>): Promise<void> {
  await fetch(`${API_BASE}/api/drafts/${token}`, {
    method: "PUT",
    headers: authHeaders(true),
    body: JSON.stringify(payload),
  });
}

/** GET /api/drafts/:token — loads a draft; returns null if not found/expired */
export async function loadDraft(token: string): Promise<(DraftPayload & { token: string }) | null> {
  try {
    const res = await fetch(`${API_BASE}/api/drafts/${token}`, {
      headers: authHeaders(),
    });
    if (!res.ok) return null;
    const json = await res.json();
    return { token, ...json.data } as DraftPayload & { token: string };
  } catch {
    return null;
  }
}

/** GET /api/drafts/mine — lists drafts owned by the current PortalUser */
export async function listMyDrafts(): Promise<PortalDraft[]> {
  const res = await fetch(`${API_BASE}/api/drafts/mine`, {
    headers: authHeaders(false, true),
  });
  if (!res.ok) throw new Error("Failed to load drafts");
  const json = await res.json();
  return json.data as PortalDraft[];
}

/** DELETE /api/drafts/:token — removes a draft after submission */
export async function deleteDraft(token: string): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/drafts/${token}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
  } catch {
    // non-critical
  }
}
